import { Circle, Community, MentionUser } from 'redux/types/account';
import {
  fetchCommunityDiscussionMentionUsers,
  fetchMentionUsers,
  fetchProjectDiscussionMentionUsers,
  fetchRequestMentionUsers,
  fetchTabEntityMentionUsers,
} from '../services/api/community';
import { store } from '../redux/store';
import { RootState } from 'StoreModel';
import { CommentType } from 'redux/types/enums';

const MENTION_MATCHING_REGEX = /@\[BabeleUser\{\d+\|@(([^\n](?!BabeleUser)){1,120})}]/gm;

function mapMentionUsers(users: MentionUser[], withAt: boolean = true) {
  return users.map(u => ({
    id: u.id,
    text: withAt ? `@${u.name || u.id.replace('@', '')}` : u.id,
    userId: u.userId,
  }));
}

export const getMentions = ({
  community,
  postType,
  projectId,
  tabEntityId,
  discussionId,
  requestId,
  circles = [],
  tags = [],
}: {
  community: Community;
  postType: CommentType;
  projectId?: number;
  tabEntityId?: number;
  discussionId?: number;
  requestId?: number;
  circles?: Circle[];
  tags?: string[];
}) => {
  return (query: string) => {
    const state: RootState = store.getState();
    const bearer = state.account.session.session.bearer;

    let cacheKey = `${postType}-${community.id}-${query}`;
    if (discussionId) cacheKey += `-${discussionId}`;
    if (tabEntityId) cacheKey += `-${tabEntityId}`;

    const cached = sessionStorage.getItem(cacheKey);
    if (cached) return Promise.resolve(JSON.parse(cached));

    switch (postType) {
      case CommentType.Discussion:
        return fetchProjectDiscussionMentionUsers(bearer, community.id, projectId!, discussionId!, query).then(res => {
          const mapped = mapMentionUsers(res.mentionUsers);
          sessionStorage.setItem(cacheKey, JSON.stringify(mapped));
          return mapped;
        });

      case CommentType.TabEntity:
        return fetchTabEntityMentionUsers(bearer, community.id, tabEntityId!, query).then(res => {
          const mapped = mapMentionUsers(res.mentionUsers);
          sessionStorage.setItem(cacheKey, JSON.stringify(mapped));
          return mapped;
        });

      case CommentType.Request:
        return fetchRequestMentionUsers(bearer, community.id, requestId!, query).then(res => {
          const mapped = mapMentionUsers(res.mentionUsers, false);
          sessionStorage.setItem(cacheKey, JSON.stringify(mapped));
          return mapped;
        });

      case CommentType.CommunityDiscussion:
        return fetchCommunityDiscussionMentionUsers(
          bearer,
          community.id,
          discussionId!,
          query,
          circles.map(c => c.id),
          tags,
        ).then(res => {
          const mapped = mapMentionUsers(res.mentionUsers);
          sessionStorage.setItem(cacheKey, JSON.stringify(mapped));
          return mapped;
        });

      default: {
        const randomId = Math.random().toString(36).substring(2, 15);
        (window as any).isFetchingMentionUsers = {
          ...((window as any).isFetchingMentionUsers || {}),
          [community.id]: randomId,
        };
        return fetchMentionUsers(bearer, community.id, query).then(response => {
          sessionStorage.setItem(cacheKey, JSON.stringify(response.mentionUsers));
          if (
            (window as any).isFetchingMentionUsers &&
            (window as any).isFetchingMentionUsers[community.id] === randomId
          ) {
            return response.mentionUsers;
          }
          return [];
        });
      }
    }
  };
};

// <a data-user-id={id} href=...>userName</a>
// converted to
// @[BabeleUser{id}]
export const mentionStringify = (content?: string): string => {
  if (!content) return '';
  const a = document.createElement('div');
  a.innerHTML = content;
  const mentions = Array.from(a.getElementsByClassName('mention'));
  mentions.forEach((mention: any) => {
    const mentioneeId = parseInt(mention.attributes['data-user-id']?.value);
    const mentioneeName = mention.attributes['data-mention']?.value;
    if (mentioneeId && mentioneeName) {
      mention.replaceWith(`@[BabeleUser{${mentioneeId}|${mentioneeName}}]`);
    }
  });
  return a.innerHTML;
};

// @[BabeleUser{id}]
// converted to
// <a data-user-id={id} href=...>userName</a>
export const mentionParse = (content?: string): string => {
  if (!content) return '';
  const re = new RegExp(MENTION_MATCHING_REGEX);
  const myArray = content.matchAll(re);
  // if we substitute the matches from beginning, the second (and next) matches will have
  // wrong indexes, so we have to do the substitutions from the end to beginning, this way
  // the indexes will still be valid
  const substitutions: { index: number; deleteCount: number; replacement: string }[] = [];
  for (const match of myArray) {
    const mentioneeIdAndName = match[0].slice(13, match[0].length - 2);
    const separatorIndex = mentioneeIdAndName.indexOf('|');
    const mentioneeId = mentioneeIdAndName.slice(0, separatorIndex);
    const mentioneeName = mentioneeIdAndName.slice(separatorIndex + 1, mentioneeIdAndName.length);
    substitutions.push({
      index: match.index || 0,
      deleteCount: match[0].length,
      replacement: `<a class="mention" data-mention="${mentioneeName}" data-user-id="${mentioneeId}" href="/user/${mentioneeId}">${mentioneeName}</a>`,
    });
  }
  substitutions.sort((a: { index: number }, b: { index: number }) => b.index - a.index);
  const splitContent = content.split('');
  substitutions.forEach((substitution: { index: number; deleteCount: number; replacement: string }) => {
    splitContent.splice(substitution.index, substitution.deleteCount, substitution.replacement);
  });
  return splitContent.join('');
};

export function MentionCustomization(editor: any) {
  editor.conversion.for('upcast').elementToAttribute({
    view: {
      name: 'a',
      key: 'data-mention',
      classes: 'mention',
      attributes: {
        'data-user-id': true,
      },
    },
    model: {
      key: 'mention',
      value: (viewItem: any) => {
        const mentionAttribute = editor.plugins.get('Mention').toMentionAttribute(viewItem, {
          userId: viewItem.getAttribute('data-user-id'),
        });
        return mentionAttribute;
      },
    },
    converterPriority: 'high',
  });
  editor.conversion.for('downcast').attributeToElement({
    model: 'mention',
    view: (modelAttributeValue: any, { writer }: any) => {
      if (!modelAttributeValue) return;
      return writer.createAttributeElement(
        'a',
        {
          class: 'mention',
          'data-mention': modelAttributeValue.id,
          'data-user-id': modelAttributeValue.userId,
          href: `/user/${modelAttributeValue.userId}`,
        },
        {
          priority: 20,
          id: modelAttributeValue.uid,
        },
      );
    },
    converterPriority: 'high',
  });
}
